import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { EyeIcon, ArrowDownTrayIcon, PlayIcon } from '@heroicons/react/24/outline';
import { openDocumentPreview } from './DocumentPreviewSlice_Store';
import { setLayoutMode } from './LayoutSlice_Store';
import { sendMessage } from './ChatSlice_Store';
import { authenticatedFetch } from './utils/authErrorHandler';
import type { SuggestedAction } from './types/redux';
import type { AppDispatch } from './types/redux';

interface SuggestedActionButtonProps {
  action: SuggestedAction;
}

/**
 * Button component for suggested actions in chat messages.
 * Handles preview_document, download_document, and send_message action types.
 */
const SuggestedActionButton: React.FC<SuggestedActionButtonProps> = ({ action }) => {
  const dispatch = useDispatch<AppDispatch>();

  // Track if one-time actions (send_message) have been clicked
  const isOneTimeAction = action.type === 'send_message';
  const [hasBeenClicked, setHasBeenClicked] = useState(false);
  const isDisabled = isOneTimeAction && hasBeenClicked;

  const handleClick = async () => {
    if (isDisabled) return;

    // Disable one-time actions immediately after click
    if (isOneTimeAction) {
      setHasBeenClicked(true);
    }

    console.log('[DealOS] 🔘 Suggested action clicked:', action);

    switch (action.type) {
      case 'preview_document': {
        // Build document object for preview
        const documentForPreview = {
          docId: action.document.docId,
          documentName: action.document.documentName,
          originalFilename: action.document.documentName,
          // Construct file path for drafts if not provided.
          // VITE_DEALOS_PR is a numeric string (e.g. "399") in PR deployments, so include the pr-{n} prefix.
          filePath: action.document.filePath || (() => {
            const pr = import.meta.env.VITE_DEALOS_PR;
            const prPrefix = pr && /^\d+$/.test(pr) ? `pr-${pr}/` : '';
            return `drafts/${prPrefix}${action.document.projectId}/${action.document.documentName}`;
          })(),
        };

        // Open document preview
        dispatch(openDocumentPreview(documentForPreview));
        // Set layout mode to workspace when document is opened
        dispatch(setLayoutMode('workspace'));
        break;
      }

      case 'download_document': {
        try {
          const filePath = action.document.filePath;
          if (!filePath) {
            console.error('[DealOS] No file path found for download');
            return;
          }

          // Get FILE_SERVICE_BASE_URL from environment
          const fileServiceUrl = import.meta.env.VITE_FILE_SERVICE_BASE_URL;
          if (!fileServiceUrl) {
            console.error('[DealOS] VITE_FILE_SERVICE_BASE_URL not configured');
            return;
          }

          // Call file-access-service to get SAS URL
          const response = await authenticatedFetch(`${fileServiceUrl}/sas/download`, {
            method: 'POST',
            body: JSON.stringify({ file_path: filePath })
          });

          if (!response.ok) {
            throw new Error(`Failed to get download URL: ${response.status}`);
          }

          const { sasUrl } = await response.json();

          // Fetch the blob and trigger download
          const downloadResponse = await fetch(sasUrl);
          const blob = await downloadResponse.blob();

          // Create object URL and trigger download
          const blobUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = action.document.documentName || 'download';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up object URL
          window.URL.revokeObjectURL(blobUrl);

          console.log('[DealOS] ✅ Download completed:', action.document.documentName);
        } catch (error) {
          console.error('[DealOS] Download failed:', error);
        }
        break;
      }

      case 'send_message': {
        // Send a message to the chat (e.g., "Continue Drafting" approval)
        const message = (action as any).message;
        if (message) {
          dispatch(sendMessage(message) as any);
          console.log('[DealOS] ✅ Sent message:', message);
        }
        break;
      }

      default:
        console.warn('[DealOS] Unknown action type:', action.type);
    }
  };

  // Get appropriate icon
  const getIcon = () => {
    switch (action.icon) {
      case 'eye':
        return <EyeIcon className="w-4 h-4" />;
      case 'download':
        return <ArrowDownTrayIcon className="w-4 h-4" />;
      case 'play':
        return <PlayIcon className="w-4 h-4" />;
      default:
        return <EyeIcon className="w-4 h-4" />;
    }
  };

  // Get button style based on action.style and disabled state
  const getButtonClassName = () => {
    const style = (action as any).style;

    // Disabled state for one-time actions after click
    if (isDisabled) {
      if (style === 'primary') {
        return "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-400 bg-gray-200 rounded-md cursor-not-allowed";
      }
      return "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-400 bg-gray-100 border border-gray-200 rounded-md cursor-not-allowed";
    }

    if (style === 'primary') {
      // Primary style: solid CVC blue button (like "Start Drafting")
      return "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-cvc-blue hover:bg-cvc-blue/90 rounded-md transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-cvc-blue focus:ring-offset-1";
    }
    // Default style: outlined CVC blue button
    return "inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-cvc-blue bg-cvc-lb2 border border-cvc-lb1 rounded-md hover:bg-cvc-lb1 hover:border-cvc-blue/50 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-cvc-blue focus:ring-offset-1";
  };

  return (
    <button
      onClick={handleClick}
      className={getButtonClassName()}
      disabled={isDisabled}
    >
      {getIcon()}
      <span>{action.label}</span>
    </button>
  );
};

export default SuggestedActionButton;
